import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  LinkOutlined,
  MailOutlined,
  PrinterOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Collapse,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useRef, useState } from "react";
import { useNotification } from "~/hooks";
import { baseService } from "~/services/base";
import { billingApi, BillingService } from "~/services/billing/billing.api";
import { createBookingInvoice } from "~/services/booking/booking.api";

const { Text } = Typography;
const { Panel } = Collapse;

interface BookingForInvoice {
  id: string;
  referenceNumber: string;
  client: { companyName: string };
  project?: { name: string };
  status: string;
  createdAt: string;
  serviceTicket: any[];
  billingData?: BillingService[];
  billingSummary?: {
    totalServices: number;
    pendingServices: number;
    invoicedServices: number;
    totalAmount: number;
  };
  isLoadingBilling?: boolean;
}

interface BookingListForInvoiceProps {
  filters?: {
    bookingStatus?: string;
    pageSize?: number;
    page?: number;
  };
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  onPaginationChange?: (page: number, pageSize?: number) => void;
  onTotalUpdate?: (total: number) => void;
  onBackToInvoiceList?: () => void;
}

export const BookingListForInvoice = ({
  filters = {},
  pagination: externalPagination,
  onPaginationChange,
  onTotalUpdate,
  onBackToInvoiceList,
}: BookingListForInvoiceProps) => {
  const [bookings, setBookings] = useState<BookingForInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [selectedBookingForInvoice, setSelectedBookingForInvoice] =
    useState<BookingForInvoice | null>(null);
  const [quickbooksConnected, setQuickbooksConnected] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Use external pagination if provided, otherwise use internal state
  const [internalPagination, setInternalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const pagination = externalPagination || internalPagination;
  const { success, error: notificationError } = useNotification();

  // Use refs to track previous values and prevent infinite loops
  const prevFiltersRef = useRef(filters);
  const prevPaginationRef = useRef(pagination);

  const checkQuickBooksConnection = async () => {
    try {
      const response = await baseService.get(
        "/api/quickbooks/connection/status"
      );
      setQuickbooksConnected(response.data.isConnected);
    } catch (error) {
      console.log("error", error);
      setQuickbooksConnected(false);
    }
  };

  const fetchBookingsForInvoice = async () => {
    try {
      setLoading(true);

      // Build query parameters from filters and pagination
      const params = new URLSearchParams();

      // Add filters
      if (filters.bookingStatus) {
        params.append("bookingStatus", filters.bookingStatus);
      }

      // Add pagination
      params.append("page", pagination.current.toString());
      params.append("pageSize", pagination.pageSize.toString());

      const response = await baseService.get(
        `/jobtickets?${params.toString()}`
      );
      const allBookings = response.data.items || [];
      const total = response.data.meta?.totalItems || response.data.total || 0;

      const bookingsWithServices = allBookings.filter(
        (booking: any) =>
          booking.serviceTicket && booking.serviceTicket.length > 0
      );

      console.log("Bookings with services:", {
        total: bookingsWithServices.length,
        bookingIds: bookingsWithServices.map((b: any) => b.id),
        allBookingsCount: allBookings.length,
        bookingsWithoutServices: allBookings.filter(
          (b: any) => !b.serviceTicket || b.serviceTicket.length === 0
        ).length,
        sampleBooking: allBookings[0]
          ? {
              id: allBookings[0].id,
              hasServiceTicket: !!allBookings[0].serviceTicket,
              serviceTicketLength: allBookings[0].serviceTicket?.length || 0,
            }
          : null,
      });

      // Format bookings using shared service
      const formattedBookings =
        billingApi.formatJobTicketsForInvoice(bookingsWithServices);

      setBookings(formattedBookings);

      // Load billing data for all bookings immediately
      setTimeout(() => {
        for (const booking of formattedBookings) {
          loadBillingData(booking.id);
        }
      }, 100);

      // Update pagination total (only if using internal pagination)
      if (!externalPagination) {
        console.log("Updating internal pagination:", {
          currentTotal: internalPagination.total,
          newTotal: total,
          bookingsWithServicesCount: bookingsWithServices.length,
        });
        setInternalPagination((prev) => ({ ...prev, total }));
      } else {
        onTotalUpdate?.(total);
      }
    } catch (error) {
      console.error("Error fetching bookings for invoice:", error);
      notificationError({
        message: "Error",
        description: "Failed to fetch bookings for invoice",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingsForInvoice();
    checkQuickBooksConnection();
  }, []);

  useEffect(() => {
    const filtersChanged =
      JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    const paginationChanged =
      JSON.stringify(pagination) !== JSON.stringify(prevPaginationRef.current);

    if (filtersChanged || paginationChanged) {
      fetchBookingsForInvoice();
      prevFiltersRef.current = filters;
      prevPaginationRef.current = pagination;
    }
  }, [filters, pagination]);

  const loadBillingData = async (bookingId: string) => {
    try {
      // Update booking state to show loading
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId
            ? { ...booking, isLoadingBilling: true }
            : booking
        )
      );

      try {
        const bookingTest = await baseService.get(`/jobtickets/${bookingId}`);
        console.log("Booking exists:", bookingTest.data);
      } catch (bookingError) {
        console.log("bookingError", bookingError);
        throw new Error(`Booking ${bookingId} not found`);
      }

      // Use the new synchronization function to ensure calculation accuracy
      const billingData = await billingApi.synchronizeBillingCalculation(bookingId);

      console.log("Billing data loaded successfully:", {
        bookingId,
        servicesCount: billingData.services?.length || 0,
        totalAmount: billingData.summary?.totalAmount || 0,
        totalServices: billingData.summary?.totalServices || 0,
      });

      // Update booking with billing data
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId
            ? {
                ...booking,
                billingData: billingData.services,
                billingSummary: billingData.summary,
                isLoadingBilling: false,
              }
            : booking
        )
      );
    } catch (error: any) {
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId
            ? {
                ...booking,
                billingData: [],
                isLoadingBilling: false,
              }
            : booking
        )
      );

      notificationError({
        message: "Error",
        description:
          error.response?.data?.message || "Failed to load billing data",
      });
    }
  };

  const onExpand = (expanded: boolean, record: BookingForInvoice) => {
    if (expanded && !record.billingData && !record.isLoadingBilling) {
      loadBillingData(record.id);
    }

    if (expanded) {
      setExpandedRows((prev) => [...prev, record.id]);
    } else {
      setExpandedRows((prev) => prev.filter((id) => id !== record.id));
    }
  };

  const handleCreateInvoice = async (booking: BookingForInvoice) => {
    console.log("handleCreateInvoice called with booking:", {
      id: booking.id,
      referenceNumber: booking.referenceNumber,
      hasBillingData: !!booking.billingData,
      billingDataLength: booking.billingData?.length || 0,
    });

    // Check if billing data is already loaded
    if (!booking.billingData || booking.billingData.length === 0) {
      console.log("No billing data found, loading billing data...");
      // Load billing data first
      try {
        await loadBillingData(booking.id);

        // Wait a bit for state to update, then check again
        setTimeout(() => {
          const updatedBooking = bookings.find((b) => b.id === booking.id);
          console.log("Updated booking after loading billing data:", {
            id: updatedBooking?.id,
            hasBillingData: !!updatedBooking?.billingData,
            billingDataLength: updatedBooking?.billingData?.length || 0,
          });

          if (
            updatedBooking &&
            updatedBooking.billingData &&
            updatedBooking.billingData.length > 0
          ) {
            console.log("Setting selected booking and showing modal");
            setSelectedBookingForInvoice(updatedBooking);
            setInvoiceModalVisible(true);
          } else {
            console.log("Still no billing data available");
            notificationError({
              message: "Error",
              description:
                "No billing data available for this booking. Please ensure the booking has completed services.",
            });
          }
        }, 500);
      } catch (error) {
        console.error("Error loading billing data:", error);
        notificationError({
          message: "Error",
          description: "Failed to load billing data for this booking",
        });
      }
    } else {
      console.log("Billing data already available, showing modal");
      setSelectedBookingForInvoice(booking);
      setInvoiceModalVisible(true);
    }
  };

  const handleQuickBooksConnect = async () => {
    try {
      const response = await baseService.get("/api/quickbooks/auth/url");
      window.open(response.data.authUrl, "_blank");
    } catch (error) {
      console.log(error);
      notificationError({
        message: "Error",
        description: "Failed to get QuickBooks authorization URL",
      });
    }
  };

  const handleCreateInvoiceConfirm = async () => {
    console.log("handleCreateInvoiceConfirm called with:", {
      selectedBookingForInvoice: selectedBookingForInvoice?.id,
      hasBillingData: !!selectedBookingForInvoice?.billingData,
      billingDataLength: selectedBookingForInvoice?.billingData?.length || 0,
    });

    if (!selectedBookingForInvoice || !selectedBookingForInvoice.billingData) {
      console.log("No billing data available, showing error");
      notificationError({
        message: "Error",
        description: "No billing data available for this booking",
      });
      return;
    }

    try {
      setCreatingInvoice(true);

      // Use the same calculation method as in Billing.tsx
      const serviceLineItems = selectedBookingForInvoice.billingData.map(
        (service) => ({
          description: service.description,
          quantity: service.quantity || 1,
          unitPrice: service.rate || 0, // Use rate from API
        })
      );

      // Get tax preference from localStorage for this booking
      const savedTaxPreference = localStorage.getItem(
        `tax_preference_${selectedBookingForInvoice.id}`
      );
      const applyTax =
        savedTaxPreference !== null ? JSON.parse(savedTaxPreference) : false;

      // Calculate totals
      const totals = billingApi.calculateTotals(
        selectedBookingForInvoice.billingData,
        selectedBookingForInvoice.id,
        applyTax
      );

      // Add tax as a separate line item if tax amount > 0
      const lineItems = [...serviceLineItems];
      if (totals.tax > 0) {
        lineItems.push({
          description: "Tax (8.875%)",
          quantity: 1,
          unitPrice: totals.tax,
        });
      }

      console.log("Created line items:", lineItems);

      // Create invoice data
      const generateInvoiceNumber = (referenceNumber: string) => {
        const baseNumber = `INV-${referenceNumber}`;
        return baseNumber;
      };

      const invoiceData = {
        lineItems,
        invoiceNumber: generateInvoiceNumber(
          selectedBookingForInvoice.referenceNumber
        ),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        memo: `Invoice for booking ${selectedBookingForInvoice.referenceNumber}`,
      };

      console.log("Invoice data to send:", invoiceData);

      // Call API to create invoice
      const result = await createBookingInvoice(
        selectedBookingForInvoice.id,
        invoiceData
      );

      console.log("Invoice created successfully:", result);

      success({
        message: "Success",
        description: `Invoice created successfully in QuickBooks. Invoice #: ${result.docNumber}`,
      });

      setInvoiceModalVisible(false);
      setSelectedBookingForInvoice(null);

      // Automatically navigate back to invoice list with refresh parameter
      if (onBackToInvoiceList) {
        // Set sessionStorage flag to indicate we're coming from create invoice
        sessionStorage.setItem("fromCreateInvoice", "true");
        // Add a small delay to show the success message before navigating
        setTimeout(() => {
          onBackToInvoiceList();
        }, 1500);
      }

      // Refresh the booking list to show updated status
      fetchBookingsForInvoice();
    } catch (error: any) {
      console.error("Error creating invoice:", error);
      notificationError({
        message: "Error",
        description:
          error.response?.data?.message ||
          "Failed to create invoice in QuickBooks",
      });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const expandedRowRender = (record: BookingForInvoice) => {
    if (!record.billingData || record.billingData.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          No billing data available
        </div>
      );
    }

    // Group services by type and calculate totals using shared service
    const groupedServices = billingApi.groupServicesByType(record.billingData);

    // Get tax preference from localStorage for this booking
    const savedTaxPreference = localStorage.getItem(
      `tax_preference_${record.id}`
    );
    const applyTax =
      savedTaxPreference !== null ? JSON.parse(savedTaxPreference) : false;

    const totals = billingApi.calculateTotals(
      record.billingData,
      record.id,
      applyTax
    );

    const serviceColumns: ColumnsType<BillingService> = [
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (text: string, service: BillingService) => (
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-sm text-gray-500">
              <Space size={16}>
                <span>
                  <CalendarOutlined />{" "}
                  {new Date(service.serviceDate).toLocaleDateString()}
                </span>
                {service.totalHours > 0 && (
                  <span>
                    <ClockCircleOutlined /> {service.totalHours}h
                  </span>
                )}
                {service.staffNames.length > 0 && (
                  <span>
                    <UserOutlined /> {service.staffNames.join(", ")}
                  </span>
                )}
              </Space>
            </div>
            {/* Show breakdown for collection/delivery services */}
            {service.serviceType === "COLLECTION" ||
            service.serviceType === "DELIVERY" ? (
              <div className="mt-2 text-xs text-gray-600">
                {/* Only show total hours for staff services */}
                {(service.serviceType === "COLLECTION" ||
                  service.serviceType === "DELIVERY" ||
                  service.serviceType === "ON_SITE_SERVICE") &&
                  service.totalHours > 0 && (
                    <div>Total Hours: {service.totalHours}h</div>
                  )}
                {/* Show combined breakdown like booking detail */}
                {service.calculationDetails?.staffCosts &&
                  service.calculationDetails.staffCosts.length > 0 && (
                    <div className="ml-4">
                      ↳ {service.calculationDetails.staffCosts[0].quantity}x{" "}
                      {service.calculationDetails.staffCosts[0].staffRoleName}s
                      +{" "}
                      {service.calculationDetails?.vesselCost &&
                      service.calculationDetails.vesselCost > 0
                        ? "Sprinter"
                        : "Vehicle"}
                      , rate{" "}
                      {billingApi.formatCurrency(
                        service.calculationDetails.staffCosts.reduce(
                          (total: number, staff: any) =>
                            total + staff.hourlyRate * staff.quantity,
                          0
                        ) +
                          (service.calculationDetails.vesselCost
                            ? service.calculationDetails.vesselCost /
                              (service.calculationDetails.hoursWorked || 1)
                            : 0)
                      )}
                      /hr × {service.totalHours}h
                    </div>
                  )}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: "Qty",
        dataIndex: "quantity",
        key: "quantity",
        width: 80,
        render: (qty: number) => qty || 1,
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        render: (rate: number) => billingApi.formatCurrency(rate || 0),
      },
      {
        title: "Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 100,
        render: (amount: number) => billingApi.formatCurrency(amount || 0),
      },
    ];

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="mb-4">
          <Row gutter={16}>
            <Col span={totals.tax > 0 ? 6 : 8}>
              <Statistic
                title="Total Services"
                value={
                  record.billingSummary?.totalServices ||
                  record.billingData?.length ||
                  0
                }
              />
            </Col>
            <Col span={totals.tax > 0 ? 6 : 8}>
              <Statistic
                title="Subtotal"
                value={totals.subtotal}
                formatter={(value) => billingApi.formatCurrency(Number(value))}
              />
            </Col>
            {totals.tax > 0 && (
              <Col span={6}>
                <Statistic
                  title="Tax (8.875%)"
                  value={totals.tax}
                  formatter={(value) =>
                    billingApi.formatCurrency(Number(value))
                  }
                />
              </Col>
            )}
            <Col span={totals.tax > 0 ? 6 : 8}>
              <Statistic
                title="Total"
                value={totals.total}
                formatter={(value) => billingApi.formatCurrency(Number(value))}
              />
            </Col>
          </Row>
        </div>

        <Collapse defaultActiveKey={Object.keys(groupedServices)}>
          {Object.entries(groupedServices).map(([serviceType, services]) => (
            <Panel
              header={`${serviceType.replace("_", " ")} Services (${
                services.length
              })`}
              key={serviceType}
            >
              <Table
                dataSource={services}
                columns={serviceColumns}
                pagination={false}
                size="small"
                rowKey="id"
              />
            </Panel>
          ))}
        </Collapse>

        <div className="mt-4 flex justify-end space-x-2">
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => handleCreateInvoice(record)}
          >
            Create Invoice
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={() => {
              // Handle print invoice
              console.log("Print invoice for booking:", record.id);
            }}
          >
            Print
          </Button>
          <Button
            icon={<MailOutlined />}
            onClick={() => {
              // Handle email invoice
              console.log("Email invoice for booking:", record.id);
            }}
          >
            Email
          </Button>
        </div>
      </div>
    );
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    if (onPaginationChange) {
      // Use external pagination handler
      onPaginationChange(page, pageSize);
    } else {
      // Use internal pagination state
      setInternalPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
    }
  };

  const mainColumns: ColumnsType<BookingForInvoice> = [
    {
      title: "Booking Reference",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Client",
      dataIndex: ["client", "companyName"],
      key: "client",
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
      key: "project",
      render: (text: string) => text || "No Project",
    },
    {
      title: "Services",
      dataIndex: "serviceTicket",
      key: "services",
      render: (services: any[]) => (
        <Space wrap>
          {services?.map((service, index) => (
            <Tag key={index} color="blue">
              {service.serviceType?.replace("_", " ")}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "COMPLETED" ? "green" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },

    {
      title: "Total Amount",
      key: "totalAmount",
      render: (_, record: BookingForInvoice) => {
        if (record.isLoadingBilling) {
          return <Spin size="small" />;
        }
        if (record.billingData) {
          // Get tax preference from localStorage for this booking
          const savedTaxPreference = localStorage.getItem(
            `tax_preference_${record.id}`
          );
          const applyTax =
            savedTaxPreference !== null
              ? JSON.parse(savedTaxPreference)
              : false;

          const totals = billingApi.calculateTotals(
            record.billingData,
            record.id,
            applyTax
          );
          return (
            <div>
              <Text strong>{billingApi.formatCurrency(totals.total)}</Text>
              <div className="text-xs text-gray-500">
                Tax: {applyTax ? "Applied" : "Excluded"}
              </div>
            </div>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
  ];

  return (
    <div className="booking-list-for-invoice">
      {/* Back to Invoice List Button */}
      {onBackToInvoiceList && (
        <div className="mb-4">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBackToInvoiceList}
            className="mb-4"
          >
            Back to Invoice List
          </Button>
        </div>
      )}

      <Table
        columns={mainColumns}
        dataSource={bookings}
        loading={loading}
        rowKey="id"
        expandable={{
          expandedRowRender,
          onExpand,
          expandedRowKeys: expandedRows,
          expandRowByClick: true,
        }}
        scroll={{ x: true }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          onChange: handlePaginationChange,
        }}
      />

      {/* Invoice Creation Modal */}
      <Modal
        title="Create QuickBooks Invoice"
        open={invoiceModalVisible}
        onCancel={() => {
          setInvoiceModalVisible(false);
          setSelectedBookingForInvoice(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setInvoiceModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="connect"
            icon={<LinkOutlined />}
            onClick={handleQuickBooksConnect}
            disabled={quickbooksConnected}
          >
            {quickbooksConnected ? "Connected" : "Connect QuickBooks"}
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={creatingInvoice}
            onClick={handleCreateInvoiceConfirm}
            disabled={
              !quickbooksConnected ||
              !selectedBookingForInvoice?.billingData ||
              selectedBookingForInvoice.billingData.length === 0
            }
          >
            Create Invoice
          </Button>,
        ]}
        width={800}
      >
        {selectedBookingForInvoice && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">
                Booking: {selectedBookingForInvoice.referenceNumber}
              </h3>
              <p className="text-gray-600">
                Client: {selectedBookingForInvoice.client.companyName}
              </p>
              {selectedBookingForInvoice.project && (
                <p className="text-gray-600">
                  Project: {selectedBookingForInvoice.project.name}
                </p>
              )}
            </div>

            {!quickbooksConnected ? (
              <Alert
                message="QuickBooks Connection Required"
                description="Please connect to QuickBooks to create invoices. Click the 'Connect QuickBooks' button below."
                type="warning"
                showIcon
                className="mb-4"
              />
            ) : !selectedBookingForInvoice.billingData ||
              selectedBookingForInvoice.billingData.length === 0 ? (
              <Alert
                message="No Billing Data Available"
                description="This booking does not have any billable services. Please ensure the booking has completed services before creating an invoice."
                type="error"
                showIcon
                className="mb-4"
              />
            ) : (
              <div>
                <div className="mb-4">
                  <Alert
                    message="QuickBooks Connected"
                    description="You can now create an invoice for this booking."
                    type="success"
                    showIcon
                  />
                </div>

                {/* Tax Status Alert */}
                {(() => {
                  const savedTaxPreference = localStorage.getItem(
                    `tax_preference_${selectedBookingForInvoice.id}`
                  );
                  const applyTax =
                    savedTaxPreference !== null
                      ? JSON.parse(savedTaxPreference)
                      : false;

                  return (
                    <div className="mb-4">
                      <Alert
                        message={`Tax Status: ${
                          applyTax ? "Applied" : "Excluded"
                        }`}
                        description={`Tax will ${
                          applyTax ? "be applied" : "not be applied"
                        } to this invoice. You can change this in the booking detail view.`}
                        type={applyTax ? "info" : "warning"}
                        showIcon
                      />
                    </div>
                  );
                })()}

                {selectedBookingForInvoice.billingData && (
                  <div>
                    <h4 className="font-medium mb-2">Invoice Summary</h4>
                    <div className="bg-gray-50 p-3 rounded">
                      <Row gutter={16}>
                        <Col span={8}>
                          <div className="text-sm text-gray-600">
                            Total Services
                          </div>
                          <div className="font-medium">
                            {selectedBookingForInvoice.billingSummary
                              ?.totalServices ||
                              selectedBookingForInvoice.billingData?.length ||
                              0}
                          </div>
                        </Col>
                        <Col span={8}>
                          <div className="text-sm text-gray-600">Subtotal</div>
                          <div className="font-medium">
                            {billingApi.formatCurrency(
                              selectedBookingForInvoice.billingData.reduce(
                                (sum, service) => sum + service.totalAmount,
                                0
                              )
                            )}
                          </div>
                        </Col>
                        <Col span={8}>
                          <div className="text-sm text-gray-600">
                            Invoice Number
                          </div>
                          <div className="font-medium">
                            {(() => {
                              const generateInvoiceNumber = (
                                referenceNumber: string
                              ) => {
                                const baseNumber = `INV-${referenceNumber}`;
                                return baseNumber;
                              };
                              return generateInvoiceNumber(
                                selectedBookingForInvoice.referenceNumber
                              );
                            })()}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Detailed breakdown */}
                    <div className="mt-4">
                      <h5 className="font-medium mb-2">Service Breakdown</h5>
                      <div className="max-h-60 overflow-y-auto">
                        {(() => {
                          const groupedServices =
                            billingApi.groupServicesByType(
                              selectedBookingForInvoice.billingData
                            );

                          // Get tax preference from localStorage for this booking
                          const savedTaxPreference = localStorage.getItem(
                            `tax_preference_${selectedBookingForInvoice.id}`
                          );
                          const applyTax =
                            savedTaxPreference !== null
                              ? JSON.parse(savedTaxPreference)
                              : false;

                          const totals = billingApi.calculateTotals(
                            selectedBookingForInvoice.billingData,
                            selectedBookingForInvoice.id,
                            applyTax
                          );

                          return (
                            <div>
                              {/* Summary totals */}
                              <div className="bg-blue-50 p-3 rounded mb-3">
                                <Row gutter={16}>
                                  <Col span={totals.tax > 0 ? 6 : 8}>
                                    <div className="text-sm text-gray-600">
                                      Total Services
                                    </div>
                                    <div className="font-medium">
                                      {selectedBookingForInvoice.billingSummary
                                        ?.totalServices ||
                                        selectedBookingForInvoice.billingData
                                          ?.length ||
                                        0}
                                    </div>
                                  </Col>
                                  <Col span={totals.tax > 0 ? 6 : 8}>
                                    <div className="text-sm text-gray-600">
                                      Subtotal
                                    </div>
                                    <div className="font-medium">
                                      {billingApi.formatCurrency(
                                        totals.subtotal
                                      )}
                                    </div>
                                  </Col>
                                  {totals.tax > 0 && (
                                    <Col span={6}>
                                      <div className="text-sm text-gray-600">
                                        Tax (8.875%)
                                      </div>
                                      <div className="font-medium">
                                        {billingApi.formatCurrency(totals.tax)}
                                      </div>
                                    </Col>
                                  )}
                                  <Col span={totals.tax > 0 ? 6 : 8}>
                                    <div className="text-sm text-gray-600">
                                      Total
                                    </div>
                                    <div className="font-medium">
                                      {billingApi.formatCurrency(totals.total)}
                                    </div>
                                  </Col>
                                </Row>
                              </div>

                              {/* Service details by type */}
                              {Object.entries(groupedServices).map(
                                ([serviceType, services]) => (
                                  <div key={serviceType} className="mb-4">
                                    <h6 className="font-medium text-sm mb-2">
                                      {serviceType.replace("_", " ")} Services (
                                      {services.length})
                                    </h6>
                                    <Table
                                      dataSource={services}
                                      columns={[
                                        {
                                          title: "Description",
                                          dataIndex: "description",
                                          key: "description",
                                          render: (
                                            text: string,
                                            service: BillingService
                                          ) => (
                                            <div>
                                              <div className="font-medium">
                                                {text}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {new Date(
                                                  service.serviceDate
                                                ).toLocaleDateString()}
                                                {service.totalHours > 0 &&
                                                  ` • ${service.totalHours}h`}
                                                {service.staffNames.length >
                                                  0 &&
                                                  ` • ${service.staffNames.join(
                                                    ", "
                                                  )}`}
                                              </div>
                                            </div>
                                          ),
                                        },
                                        {
                                          title: "Qty",
                                          dataIndex: "quantity",
                                          key: "quantity",
                                          width: 60,
                                          render: (qty: number) => qty || 1,
                                        },
                                        {
                                          title: "Rate",
                                          dataIndex: "rate",
                                          key: "rate",
                                          width: 80,
                                          render: (rate: number) =>
                                            billingApi.formatCurrency(
                                              rate || 0
                                            ),
                                        },
                                        {
                                          title: "Amount",
                                          dataIndex: "totalAmount",
                                          key: "totalAmount",
                                          width: 80,
                                          render: (amount: number) =>
                                            billingApi.formatCurrency(
                                              amount || 0
                                            ),
                                        },
                                      ]}
                                      pagination={false}
                                      size="small"
                                      rowKey="id"
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
